import { ThrowLiteError } from '../lite-error.js';
import { _setDerivedTexture2DHook } from '../texture/texture-2d.js';

const TEXTURE_PRUNE_FLOOR = 64;
let _sourceOwners = null;
function stampTexture(state, tex, source) {
  tex._recoverySource = source;
  const textures = state._textures;
  if (textures.size >= state._texturesPruneAt) {
    for (const ref of textures) {
      if (!ref.deref()) {
        textures.delete(ref);
      }
    }
    state._texturesPruneAt = Math.max(TEXTURE_PRUNE_FLOOR, textures.size * 2);
  }
  textures.add(new WeakRef(tex));
}
function trackDerivedTexture(base, derived) {
  const source = base._recoverySource;
  if (!source) {
    return;
  }
  const state = _sourceOwners?.get(source)?.deref();
  if (state) {
    stampTexture(state, derived, source);
  }
}
function attachRecoveryCapture(engine) {
  const state = engine._deviceLostRecovery;
  const owner = new WeakRef(state);
  const stamp = (tex, source) => {
    (_sourceOwners ??= /* @__PURE__ */ new WeakMap()).set(source, owner);
    stampTexture(state, tex, source);
  };
  _setDerivedTexture2DHook(trackDerivedTexture);
  engine._dlr = {
    t: stamp,
    d: trackDerivedTexture,
    u(tex, url, opts) {
      stamp(tex, { kind: "url", url, opts: { ...opts } });
    },
    s(tex, r, g, b, a) {
      stamp(tex, { kind: "solid", rgba: [r, g, b, a] });
    },
    b(tex, bitmap, srgb, mipMaps, fallback) {
      stamp(tex, {
        kind: "bitmap",
        bitmap,
        srgb,
        mipMaps,
        fallback
      });
    },
    p(tex, data, options) {
      stamp(tex, {
        kind: "pixels",
        data: data.slice(0, tex.width * tex.height * 4),
        width: tex.width,
        height: tex.height,
        options: { ...options }
      });
    },
    r(tex, width, height, format, samplerDesc) {
      stamp(tex, { kind: "render", width, height, format, samplerDesc });
    },
    w(tex, data, x, y, width, height, dataOffset = 0, bytesPerRow = width * 4) {
      const source = tex._recoverySource;
      if (source?.kind !== "pixels") {
        return;
      }
      const rowBytes = width * 4;
      for (let row = 0; row < height; row++) {
        const srcStart = dataOffset + row * bytesPerRow;
        const dstStart = ((y + row) * source.width + x) * 4;
        source.data.set(data.subarray(srcStart, srcStart + rowBytes), dstStart);
      }
    },
    m(mesh, uv2s, tangents, colors, gpuIndices, indexFormat) {
      if (!engine._deviceLostRecovery?._meshCaptureRefs) {
        return;
      }
      mesh._cpuUv2s = uv2s ?? null;
      mesh._cpuTangents = tangents ?? null;
      mesh._cpuColors = colors ?? null;
      mesh._cpuGpuIndices = gpuIndices;
      mesh._cpuIndexFormat = indexFormat;
    },
    e(scene, url, brdfUrl) {
      if (state._meshCaptureRefs) {
        scene._envRecoverySource = { kind: "env", url, brdfUrl };
      }
    },
    h(scene, url, faceSize) {
      if (state._meshCaptureRefs) {
        scene._envRecoverySource = { kind: "hdr", url, faceSize };
      }
    }
  };
}
function _retainDeviceLostRecoveryCapture(engine, includeMeshes = false) {
  const state = engine._deviceLostRecovery;
  if (!state) {
    ThrowLiteError(42);
  }
  state._captureRefs++;
  if (includeMeshes) {
    state._meshCaptureRefs++;
  }
  if (state._captureRefs === 1) {
    attachRecoveryCapture(engine);
  }
}
function _releaseDeviceLostRecoveryCapture(engine, includeMeshes = false) {
  const state = engine._deviceLostRecovery;
  if (!state || state._captureRefs === 0) {
    return;
  }
  state._captureRefs--;
  if (includeMeshes && state._meshCaptureRefs > 0) {
    state._meshCaptureRefs--;
  }
  if (state._captureRefs === 0) {
    engine._dlr = void 0;
  }
}

export { _releaseDeviceLostRecoveryCapture, _retainDeviceLostRecoveryCapture };
//# sourceMappingURL=device-lost-recovery-capture.js.map
